import { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import decodeJWT from "jwt-decode";
import useSWR from "swr";

const getUser = async () => {
  const res = await axios.get("/user/whoami");
  if (!res) return;
  window.localStorage.setItem("token", res.data.accessToken);
  return res.data;
};

const accessToken = window.localStorage.getItem("token");
const user = accessToken ? decodeJWT(accessToken) : null;
const initialState = { accessToken, user };

export const useAuth = () => {
  const history = useHistory();
  const location = useLocation();

  const { data, error, mutate } = useSWR("/user/whoami", getUser, {
    shouldRetryOnError: false,
    fallbackData: initialState,
    dedupingInterval: 60 * 60 * 1000,
  });

  const login = useCallback(
    async ({ loginData, redirectTo = "/" }) => {
      const { data } = await axios.post("/user/login", loginData);
      window.localStorage.setItem("token", data.accessToken);
      mutate(data, false);
      console.log(
        "Login succeed! Redirecting to:",
        redirectTo.includes("login") || !redirectTo || redirectTo.length < 1 ? "/" : redirectTo,
        {
          redirectTo,
          includesLogin: redirectTo.includes("login"),
          isNil: !redirectTo,
          empty: redirectTo?.length < 1,
        }
      );
      history.push(
        redirectTo.includes("login") || !redirectTo || redirectTo.length < 1 ? "/" : redirectTo
      );
    },
    [history, mutate]
  );

  const nexusLogin = useCallback(
    async ({ hash, redirectTo = "/" }) => {
      const { data } = await axios.post("/user/nexus-login", { hash });
      if (!data) {
        console.log("NEXUS Login failed");
        history.push("/login");
        return;
      }
      console.log("ACCESS TOKEN:", data.accessToken);
      window.localStorage.setItem("token", data.accessToken);
      mutate(data, false);
      console.log(
        "NEXUS Login succeed! Redirecting to:",
        redirectTo.includes("login") || !redirectTo || redirectTo.length < 1 ? "/" : redirectTo,
        {
          redirectTo,
          includesLogin: redirectTo.includes("login"),
          isNil: !redirectTo,
          empty: redirectTo?.length < 1,
        }
      );
      history.push(
        redirectTo.includes("login") || !redirectTo || redirectTo.length < 1 ? "/" : redirectTo
      );
    },
    [history, mutate]
  );

  const signout = useCallback(() => {
    window.localStorage.removeItem("token");
    history.push("/login");
    mutate();
  }, [history, mutate]);

  useEffect(() => {
    async function fetch() {
      const isLogin = window.location.pathname.includes("login");
      const hasSession =
        Boolean(data?.accessToken) && decodeJWT(data?.accessToken).exp * 1000 > Date.now();

      if (hasSession && isLogin) return history.push("/tickets_nexus");
      if (hasSession) return;

      const searchParams = new URLSearchParams(location.search);
      const hash = searchParams.get("hash");

      if (hash) return await nexusLogin({ hash, redirectTo: window.location.pathname });
      if (isLogin && !hash) return;
      if (!hash) return history?.replace({ pathname: "/login" });
    }

    fetch().catch((err) => {
      console.log("Error fetching", err);
      history.push("/login");
    });
  }, [location, data, history, nexusLogin]);

  const isAuth = useMemo(
    () => Boolean(data?.accessToken) && decodeJWT(data?.accessToken).exp * 1000 > Date.now(),
    [data?.accessToken]
  );

  const user = useMemo(() => data?.user || {}, [data.user]);

  return {
    isLoading: !data && !error,
    isAuth,
    error,
    mutate,
    user,
    login,
    signout,
  };
};
